import { useAuth } from '@/hooks/use-auth'
import { getUserDisplayName } from '@/types'
import { ChevronDown, LogOut } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

export function AgentStandaloneLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [logoLoadFailed, setLogoLoadFailed] = useState(false)

  const displayName = getUserDisplayName(user)
  const displayInitial = displayName[0]?.toUpperCase() || 'U'

  useEffect(() => {
    if (!showUserMenu) return
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-user-menu]')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-[#102A56]">
      <header className="h-14 shrink-0 flex items-center justify-between gap-4 bg-[#102A56] px-5">
        <a href="/resume" className="flex items-center shrink-0">
          <div className="h-[25px] w-auto max-w-[126px] overflow-hidden bg-transparent shrink-0">
            {!logoLoadFailed ? (
              <img
                src="/logomark-white.png"
                alt="Pelgo"
                className="h-full w-full object-contain object-left"
                onError={() => setLogoLoadFailed(true)}
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-white font-bold text-sm">
                P
              </div>
            )}
          </div>
        </a>

        <div className="flex items-center gap-3 shrink-0">
          <div data-user-menu className="relative shrink-0">
            <button
              type="button"
              onClick={() => setShowUserMenu((prev) => !prev)}
              className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-white/10 border border-transparent hover:border-white/20"
            >
              <div className="w-8 h-8 rounded-full border flex items-center justify-center text-xs font-medium shrink-0 bg-white border-white text-[#9CA3AF]">
                {displayInitial}
              </div>
              <span className="text-sm font-medium text-white max-w-[140px] truncate">
                {displayName}
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 shrink-0 text-white/80 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
              />
            </button>

            {showUserMenu && (
              <div className="absolute top-full right-0 mt-1 min-w-[180px] rounded-lg border bg-background shadow-lg py-1 z-50">
                <button
                  type="button"
                  onClick={async () => {
                    setShowUserMenu(false)
                    try {
                      await logout()
                      navigate('/login', { replace: true })
                    } catch (error) {
                      console.error('Logout failed:', error)
                    }
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-muted transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 rounded-t-lg bg-[#F3F4F6] overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
